<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ChevronDown, Check } from 'lucide-vue-next'

/**
 * One filter, one control.
 *
 * The listing used to hide subjects, levels and exam boards inside a single
 * "Filters" dropdown, so choosing a subject took three clicks and nothing on
 * screen said what was already applied. Each dimension now gets its own button
 * that shows its own state.
 *
 * Multi-select: options toggle, and the change is emitted on close rather than
 * on every tick, so choosing three subjects is one request instead of three.
 */
const props = defineProps({
  label: { type: String, required: true },
  options: { type: Array, default: () => [] },
  modelValue: { type: Array, default: () => [] },
  /** Reads options as `{ slug, name }` unless told otherwise. */
  valueKey: { type: String, default: 'slug' },
  labelKey: { type: String, default: 'name' },
})

const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)
const root = ref(null)
const draft = ref([...props.modelValue])

const selectedCount = computed(() => props.modelValue.length)

const summary = computed(() => {
  if (!selectedCount.value) return props.label
  if (selectedCount.value === 1) {
    const match = props.options.find((o) => o[props.valueKey] === props.modelValue[0])
    return match ? match[props.labelKey] : props.label
  }
  return `${props.label} · ${selectedCount.value}`
})

/**
 * Ticking a box filters immediately.
 *
 * The first version held the selection back until the panel closed, to save a
 * request. That is the wrong trade: a filter that does nothing when you press it
 * reads as broken, and people press it again. The results update under the open
 * panel, which is also how you discover that a combination has no tutors.
 */
const toggle = (value) => {
  const index = draft.value.indexOf(value)
  index > -1 ? draft.value.splice(index, 1) : draft.value.push(value)

  emit('update:modelValue', [...draft.value])
  emit('change')
}

const close = () => {
  if (!open.value) return
  open.value = false
}

const openPanel = () => {
  draft.value = [...props.modelValue]
  open.value = true
}

const onDocumentClick = (event) => {
  if (root.value && !root.value.contains(event.target)) close()
}

const onKeydown = (event) => {
  if (event.key === 'Escape') close()
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div ref="root" class="relative">
    <button
      type="button"
      class="flex h-11 w-full items-center justify-between gap-2 rounded-md border px-4 text-sm font-semibold transition-colors sm:w-auto"
      :class="selectedCount
        ? 'border-brand bg-brand-soft text-brand-soft-text'
        : 'border-line bg-surface text-ink hover:bg-surface-2'"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="open ? close() : openPanel()"
    >
      <span class="truncate">{{ summary }}</span>
      <ChevronDown class="size-4 shrink-0 opacity-60 transition-transform duration-200" :class="{ 'rotate-180': open }" />
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      leave-active-class="transition duration-100 ease-in"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="open"
        class="absolute left-0 top-full z-30 mt-2 max-h-80 w-[min(20rem,calc(100vw-2.5rem))] overflow-y-auto rounded-lg border border-line bg-surface p-2 shadow-lg"
        role="listbox"
        :aria-label="label"
      >
        <p v-if="!options.length" class="px-3 py-4 text-sm text-muted">Nothing to filter on yet.</p>

        <label
          v-for="option in options"
          :key="option[valueKey]"
          class="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-surface-2"
        >
          <span
            class="grid size-4.5 shrink-0 place-items-center rounded-sm border transition-colors"
            :class="draft.includes(option[valueKey]) ? 'border-brand bg-brand text-brand-contrast' : 'border-line-strong'"
          >
            <Check v-if="draft.includes(option[valueKey])" class="size-3" />
          </span>
          <input
            type="checkbox"
            class="sr-only"
            :value="option[valueKey]"
            :checked="draft.includes(option[valueKey])"
            @change="toggle(option[valueKey])"
          />
          <span class="text-ink">{{ option[labelKey] }}</span>
        </label>
      </div>
    </Transition>
  </div>
</template>
